// ─────────────────────────────────────────────
// KLOTHIQ — Status Badge Component (Dark Theme)
// ─────────────────────────────────────────────

import { cn } from '@/lib/utils';
import { getStatusBadge } from '@klothiq/design-tokens';

export interface StatusBadgeProps {
  status: string;
  className?: string;
  size?: 'sm' | 'md';
}

// Dark-optimized badge colors — more vibrant on dark backgrounds
const COLOR_CLASSES = {
  green:  'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  amber:  'bg-amber-500/15   text-amber-400   border-amber-500/25',
  red:    'bg-red-500/15     text-red-400     border-red-500/25',
  blue:   'bg-cyan-500/15    text-cyan-400    border-cyan-500/25',
  purple: 'bg-purple-500/15  text-purple-400  border-purple-500/25',
  grey:   'bg-slate-500/15   text-slate-400   border-slate-500/25',
} as const;

// Light-theme fallback (used when .dark class is absent)
const COLOR_CLASSES_LIGHT = {
  green:  'bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]',
  amber:  'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]',
  red:    'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]',
  blue:   'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]',
  purple: 'bg-[#F3E8FF] text-[#6B21A8] border-[#E9D5FF]',
  grey:   'bg-[#F4F4F5] text-[#3F3F46] border-[#E4E4E7]',
} as const;

const DOT_CLASSES = {
  green:  'bg-emerald-400',
  amber:  'bg-amber-400',
  red:    'bg-red-400',
  blue:   'bg-cyan-400',
  purple: 'bg-purple-400',
  grey:   'bg-slate-400',
} as const;

export default function StatusBadge({ status, className, size = 'md' }: StatusBadgeProps) {
  const config = getStatusBadge(status);
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const colorClass = isDark ? COLOR_CLASSES[config.color] : COLOR_CLASSES_LIGHT[config.color];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs',
        colorClass,
        className,
      )}
    >
      <span className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', DOT_CLASSES[config.color])} />
      {config.label}
    </span>
  );
}
